import React, { ReactNode, useEffect, useMemo, useRef } from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { COLORS } from "components/v3/Theme/colors";
import SuperStepDot, { SuperStepState } from "./SuperStepDot";
import { SubStepType } from "./SubStep";
import { calculateStepState } from "./utils";
import VerticalStepItem from "./VerticalStepItem";

export interface Step {
  onClick?: (params: { index: number; isComplete: boolean }) => undefined;
  stepIndex: number;
  shortName: string;
  longName: string;
  subSteps: SubStepType[];
}

export type StepperLayout = "responsive" | "horizontal" | "vertical";

export type HorizontalStripSurface = "solid" | "transparent";

export interface StepperProps {
  activeStepIndex: number;
  greeting?: ReactNode;
  headerShown?: boolean;
  /**
   * Horizontal strip only. `transparent` removes the white bar and shadow and
   * keeps steps grouped on the left (no flex stretch across the row).
   */
  horizontalStripSurface?: HorizontalStripSurface;
  /** When `horizontal`, always use the top horizontal strip (all breakpoints). */
  layout?: StepperLayout;
  showMobileGreeting?: boolean;
  steps: Step[];
}

export interface StepItemProps {
  menuItemIndex: number;
  stepIndex: number;
  label: string;
  activeStepIndex: number;
  subSteps?: SubStepType[];
}

const Stepper: React.FC<StepperProps> = ({
  activeStepIndex,
  greeting,
  headerShown = false,
  horizontalStripSurface = "solid",
  layout = "responsive",
  showMobileGreeting = false,
  steps = []
}) => {
  const horizonalListItemRefs = useRef([]);
  const theme = useTheme();
  const screenWidthIsLgOrSmaller = useMediaQuery(theme.breakpoints.down("lg"));

  const isHorizontal = useMemo(
    () =>
      layout === "horizontal" ||
      (layout === "responsive" && screenWidthIsLgOrSmaller),
    [layout, screenWidthIsLgOrSmaller]
  );

  const renderVerticalSteps = () => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        position="relative"
      >
        <Box
          sx={{
            position: "absolute",
            top: 30,
            bottom: 60,
            left: "19px",
            borderLeft: `2px dashed ${COLORS.ACCENT2}`
          }}
        />
        {steps.map((step, index) => {
          return (
            <VerticalStepItem
              menuItemIndex={index}
              activeStepIndex={activeStepIndex}
              key={step.stepIndex}
              step={step}
            />
          );
        })}
      </Box>
    );
  };

  const renderFullVerticalStepper = () => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "30px",
          paddingLeft: "50px"
        }}
      >
        {!!greeting && greeting}
        {renderVerticalSteps()}
      </Box>
    );
  };

  const renderHorizontalStepper = () => {
    let barPosition: "fixed" | "relative" | "sticky" = "fixed";
    if (layout === "horizontal") {
      barPosition = "relative";
    } else if (headerShown) {
      barPosition = "sticky";
    }

    const stripTransparent = horizontalStripSurface === "transparent";

    return (
      <Box
        component="ul"
        sx={{
          alignItems: "center",
          alignSelf: "flex-start",
          backgroundColor: stripTransparent ? "transparent" : COLORS.WHITE,
          boxShadow: stripTransparent ? "none" : "0px 4px 12px rgba(0, 0, 0, 0.1)",
          boxSizing: "border-box",
          display: "flex",
          flexGrow: stripTransparent ? 0 : 1,
          gap: stripTransparent ? "30px" : "15px",
          height: stripTransparent ? "auto" : "50px",
          left: 0,
          margin: 0,
          maxHeight: stripTransparent ? "none" : "50px",
          minHeight: stripTransparent ? "auto" : undefined,
          msOverflowStyle: "none",
          overflowX: "scroll",
          overflowY: "hidden",
          padding: stripTransparent ? "8px 12px" : "15px 15px 15px 10px",
          position: barPosition,
          scrollbarWidth: "none",
          top: barPosition === "relative" ? undefined : 0,
          whiteSpace: "nowrap",
          width: stripTransparent ? "max-content" : "100%",
          zIndex: barPosition === "relative" ? undefined : 1000
        }}
      >
        {steps.map((step, index) => {
          const { stepState, superStepColoration } = calculateStepState(
            step,
            activeStepIndex
          );
          const overriddenStepState = [
            SuperStepState.ChildIsActive,
            SuperStepState.Active
          ].includes(stepState)
            ? SuperStepState.Active
            : stepState;
          const { onClick } = step;
          const isComplete = activeStepIndex > step.stepIndex;

          return (
            <Box
              component="li"
              key={step.stepIndex}
              onClick={() => onClick?.({ index: step.stepIndex, isComplete })}
              ref={(element) => {
                horizonalListItemRefs.current[index] = element;
              }}
              sx={{
                alignItems: "center",
                cursor: onClick && isComplete ? "pointer" : "default",
                display: "flex",
                flexGrow: stripTransparent ? 0 : 1,
                flexDirection: "row",
                position: "relative",
                width: "max-content"
              }}
            >
              <Box
                sx={{
                  alignItems: "center",
                  display: "flex",
                  gap: "10px",
                  justifyContent: "center"
                }}
              >
                <SuperStepDot state={overriddenStepState} index={index + 1} />
                <Typography
                  sx={{
                    typography: {
                      xs: `body_strong_xs_${superStepColoration}`,
                      lg: `body_strong_${superStepColoration}`
                    }
                  }}
                >
                  {step.shortName}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  useEffect(() => {
    if (
      isHorizontal &&
      horizonalListItemRefs.current[activeStepIndex] &&
      activeStepIndex > 0
    ) {
      horizonalListItemRefs.current[activeStepIndex].scrollIntoView({
        behavior: "instant",
        block: "end",
        inline: "start" // Scroll to the left edge of the element
      });
    }
  }, [activeStepIndex, isHorizontal]);

  if (isHorizontal) {
    const putGreetingAfterBar =
      layout === "responsive" && screenWidthIsLgOrSmaller;
    const showGreetingInHorizontal =
      !!greeting &&
      (layout === "horizontal" ||
        (layout === "responsive" && showMobileGreeting));

    return (
      <Box
        sx={{
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap:
            layout === "horizontal"
              ? { lg: (theme) => `calc(${theme.spacing(2)} + 15px)`, xs: "35px" }
              : { lg: 2, xs: "20px" },
          width: "100%"
        }}
      >
        {!putGreetingAfterBar && showGreetingInHorizontal ? greeting : null}
        {renderHorizontalStepper()}
        {putGreetingAfterBar && showGreetingInHorizontal ? greeting : null}
      </Box>
    );
  }

  return renderFullVerticalStepper();
};

export default Stepper;
